/**
 * WorkItemQuickChips
 * ------------------------------------------------------------------
 * ピン留め + 使用頻度上位 6 件を常設し、クリックで即 1 行追加する。
 * 設計: docs/design/work-item-picker-ux.md §6.3
 *  - チップにも単価を出す（D5）
 *  - 末尾に業務マスタ管理への導線（D0/D4）
 */
import { Link } from "react-router-dom";
import { formatJPY } from "@juro/shared";
import type { WorkItemMaster } from "../../api/workItemMasterApi.js";
import { masterItemAmount, singleItemOf } from "./masterOptions.js";
import { useApplyMaster } from "./useApplyMaster.js";
import { toast } from "../Toast.js";
import { PROFESSION_COLOR } from "../../lib/workItemConstants.js";

type Props = {
  caseId:  string;
  masters: WorkItemMaster[];
};

const MAX_CHIPS = 6;

export function WorkItemQuickChips({ caseId, masters }: Props) {
  // API 側で is_pinned DESC, usage_count DESC 順に返る。
  // ここでさらに「ピン留め or 使用実績あり」に絞り、単一明細マスタは単価 0 のものを除く。
  // ビルトインの業務マスタは既定で単価 0 の複数明細束（apps/api/src/lib/tenant-defaults.ts）
  // のため、絞り込まないと初期状態で ¥0 の行が並ぶチップだらけになる（設計 §6.3 の意図に反する）。
  const eligible = masters.filter((master) => {
    if (!master.is_pinned && master.usage_count < 1) return false;
    const single = singleItemOf(master);
    if (single && masterItemAmount(single) === 0) return false;
    return true;
  });
  const top = eligible.slice(0, MAX_CHIPS);
  const applyMut = useApplyMaster(caseId);

  const manageLink = (
    <Link
      to="/settings/work-items"
      style={{
        fontSize: 11, fontWeight: 700, color: "#0f766e", textDecoration: "none",
        border: "1px dashed #99f6e4", borderRadius: 99, padding: "4px 10px",
      }}
    >
      業務マスタを管理
    </Link>
  );

  return (
    <div style={{
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 10,
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginRight: 2 }}>
        よく使う
      </span>

      {top.length === 0 && (
        <span style={{ fontSize: 11, color: "#94a3b8" }}>
          {masters.length === 0
            ? "業務マスタが未登録です。よく使う業務を登録すると 1 クリックで追加できます。"
            : "ピン留めまたは使用実績があり、単価が設定された業務マスタがありません。"}
        </span>
      )}

      {top.map((master) => {
        const single = singleItemOf(master);
        const profession = single?.profession ?? master.profession;
        const color = PROFESSION_COLOR[profession] ?? "#475569";
        const label = single?.title || master.name;

        return (
          <button
            key={master.id}
            type="button"
            disabled={applyMut.isPending}
            onClick={() =>
              applyMut.mutate(
                { masterId: master.id },
                { onSuccess: () => toast(`${label} を追加しました`) },
              )
            }
            title={
              single
                ? `${label} ${formatJPY(masterItemAmount(single))} を 1 行追加`
                : `${master.name}（${master.default_items.length} 行）を追加`
            }
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#fff",
              border: `1px solid ${color}33`,
              borderLeft: `3px solid ${color}`,
              borderRadius: 99,
              padding: "4px 11px",
              fontSize: 12, fontWeight: 700, color: "#0f172a",
              fontFamily: "inherit",
              cursor: applyMut.isPending ? "wait" : "pointer",
              maxWidth: 260,
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {master.is_pinned ? "★ " : ""}{label}
            </span>
            {single ? (
              <span style={{ color: "#0f766e", fontWeight: 700 }}>
                {formatJPY(masterItemAmount(single))}
              </span>
            ) : (
              <span style={{ color: "#c2410c", fontWeight: 700 }}>
                {master.default_items.length}行
              </span>
            )}
          </button>
        );
      })}

      {manageLink}
    </div>
  );
}
